import { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Tooltip,
  Chip,
  IconButton,
} from "@mui/material";
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import {
  format,
  startOfYear,
  endOfYear,
  eachMonthOfInterval,
  isSameMonth,
  parseISO,
} from "date-fns";

type EmploymentType =
  | "Full time"
  | "intern"
  | "Pixel Pilot"
  | "Part time"
  | "contract";

interface EmploymentPeriod {
  id: number;
  position: string;
  employment_type: EmploymentType;
  start_date: string;
  end_date: string | null;
  is_current: boolean;
}

interface Employee {
  id: number;
  name: string;
  employmentHistory: EmploymentPeriod[];
}

interface EmploymentHistoryCalendarProps {
  employees: Employee[];
  singleUserView?: boolean;
}

const EMPLOYMENT_TYPE_COLORS: Record<
  EmploymentType,
  { bg: string; text: string }
> = {
  "Full time": { bg: "#4CAF50", text: "#FFFFFF" },
  intern: { bg: "#2196F3", text: "#FFFFFF" },
  "Pixel Pilot": { bg: "#9C27B0", text: "#FFFFFF" },
  "Part time": { bg: "#FF9800", text: "#FFFFFF" },
  contract: { bg: "#F44336", text: "#FFFFFF" },
};

export default function EmploymentHistoryCalendar({
  employees,
  singleUserView = false,
}: EmploymentHistoryCalendarProps) {
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  const yearStart = startOfYear(new Date(currentYear, 0, 1));
  const yearEnd = endOfYear(new Date(currentYear, 0, 1));

  const months = useMemo(() => {
    return eachMonthOfInterval({ start: yearStart, end: yearEnd });
  }, [yearStart, yearEnd]);

  const totalMonths = months.length;
  const monthWidth = 80; // Width of each month column in pixels

  const handlePreviousYear = () => {
    setCurrentYear((prev) => prev - 1);
  };

  const handleNextYear = () => {
    setCurrentYear((prev) => prev + 1);
  };

  // Check if an employment period overlaps with the current year
  const isPeriodInYear = (
    startDate: string,
    endDate: string | null,
    isCurrent: boolean,
  ) => {
    const start = parseISO(startDate);
    const end = isCurrent
      ? new Date()
      : endDate
        ? parseISO(endDate)
        : new Date();
    const yearStart = startOfYear(new Date(currentYear, 0, 1));
    const yearEnd = endOfYear(new Date(currentYear, 0, 1));

    // Check if the period overlaps with the current year
    return start <= yearEnd && end >= yearStart;
  };

  const getMonthIndex = (date: string) => {
    const parsedDate = parseISO(date);
    let index = 0;
    for (const month of months) {
      if (isSameMonth(month, parsedDate)) {
        return index;
      }
      index++;
    }
    return -1;
  };

  const getBarWidth = (
    startDate: string,
    endDate: string | null,
    isCurrent: boolean,
  ) => {
    const start = parseISO(startDate);
    const end = isCurrent
      ? new Date()
      : endDate
        ? parseISO(endDate)
        : new Date();
    const yearStart = startOfYear(new Date(currentYear, 0, 1));
    const yearEnd = endOfYear(new Date(currentYear, 0, 1));

    // Calculate the visible start and end within the current year
    const visibleStart = start < yearStart ? yearStart : start;
    const visibleEnd = end > yearEnd ? yearEnd : end;

    const startIndex = getMonthIndex(visibleStart.toISOString());
    const endIndex = getMonthIndex(visibleEnd.toISOString());

    if (startIndex === -1 || endIndex === -1) return 0;

    const width = (endIndex - startIndex + 1) * monthWidth;
    return Math.max(width, monthWidth); // Minimum width of 1 month
  };

  const getBarLeft = (startDate: string) => {
    const start = parseISO(startDate);
    const yearStart = startOfYear(new Date(currentYear, 0, 1));

    // If the start date is before the current year, start from the beginning of the year
    const visibleStart = start < yearStart ? yearStart : start;
    const startIndex = getMonthIndex(visibleStart.toISOString());

    if (startIndex === -1) return 0;
    return startIndex * monthWidth;
  };

  return (
    <Card sx={{ boxShadow: 0 }}>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            {singleUserView
              ? "Employment History"
              : "Employee Employment History"}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton onClick={handlePreviousYear} size="small">
                <ChevronLeftIcon />
              </IconButton>
              <Typography variant="body1" fontWeight="bold">
                {currentYear}
              </Typography>
              <IconButton onClick={handleNextYear} size="small">
                <ChevronRightIcon />
              </IconButton>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            overflowX: "auto",
            overflowY: "auto",
            maxHeight: 600,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          <Box sx={{ minWidth: totalMonths * monthWidth + 200 }}>
            {/* Header Row - Years and Months */}
            <Box
              sx={{
                display: "flex",
                position: "sticky",
                top: 0,
                zIndex: 2,
                backgroundColor: "background.paper",
                borderBottom: "2px solid",
                borderColor: "divider",
              }}
            >
              {/* Employee Name Column - only show in multi-user view */}
              {!singleUserView && (
                <Box
                  sx={{
                    width: 200,
                    minWidth: 200,
                    p: 2,
                    borderRight: "1px solid",
                    borderColor: "divider",
                    backgroundColor: "background.paper",
                    position: "sticky",
                    left: 0,
                    zIndex: 3,
                  }}
                >
                  <Typography variant="subtitle2" fontWeight="bold">
                    Employee
                  </Typography>
                </Box>
              )}

              {/* Timeline Header */}
              <Box sx={{ display: "flex" }}>
                {months.map((month, index) => {
                  const year = format(month, "yyyy");
                  const monthName = format(month, "MMM");
                  const showYear =
                    index === 0 || format(months[index - 1], "yyyy") !== year;

                  return (
                    <Box
                      key={index}
                      sx={{
                        width: monthWidth,
                        minWidth: monthWidth,
                        textAlign: "center",
                        borderRight: "1px solid",
                        borderColor: "divider",
                        position: "relative",
                      }}
                    >
                      {showYear && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            backgroundColor: "primary.main",
                            color: "primary.contrastText",
                            py: 0.5,
                            fontSize: "0.75rem",
                            fontWeight: "bold",
                          }}
                        >
                          {year}
                        </Box>
                      )}
                      <Box
                        sx={{
                          pt: showYear ? 5 : 2,
                          pb: 1,
                          fontSize: "0.7rem",
                          color: "text.secondary",
                        }}
                      >
                        {monthName}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {/* Employee Rows */}
            {employees.map((employee) => (
              <Box
                key={employee.id}
                sx={{
                  display: "flex",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
              >
                {/* Employee Name - only show in multi-user view */}
                {!singleUserView && (
                  <Box
                    sx={{
                      width: 200,
                      minWidth: 200,
                      p: 2,
                      borderRight: "1px solid",
                      borderColor: "divider",
                      position: "sticky",
                      left: 0,
                      backgroundColor: "background.paper",
                      zIndex: 1,
                    }}
                  >
                    <Typography variant="body2" fontWeight="medium" noWrap>
                      {employee.name}
                    </Typography>
                  </Box>
                )}

                {/* Timeline Bars */}
                <Box
                  sx={{
                    flex: 1,
                    position: "relative",
                    height: 60,
                    p: 1,
                  }}
                >
                  {employee.employmentHistory.map((period) => {
                    // Only show if the period overlaps with the current year
                    if (
                      !isPeriodInYear(
                        period.start_date,
                        period.end_date,
                        period.is_current,
                      )
                    )
                      return null;

                    const barWidth = getBarWidth(
                      period.start_date,
                      period.end_date,
                      period.is_current,
                    );
                    const barLeft = getBarLeft(period.start_date);
                    const colors =
                      EMPLOYMENT_TYPE_COLORS[period.employment_type];

                    if (barWidth <= 0) return null;

                    return (
                      <Tooltip
                        key={period.id}
                        title={
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {period.position}
                            </Typography>
                            <Typography variant="caption">
                              {period.employment_type}
                            </Typography>
                            <br />
                            <Typography variant="caption">
                              {format(parseISO(period.start_date), "MMM yyyy")}{" "}
                              -{" "}
                              {period.is_current
                                ? "Present"
                                : period.end_date
                                  ? format(
                                      parseISO(period.end_date),
                                      "MMM yyyy",
                                    )
                                  : "N/A"}
                            </Typography>
                          </Box>
                        }
                      >
                        <Box
                          sx={{
                            position: "absolute",
                            left: barLeft,
                            width: barWidth,
                            height: 40,
                            top: 10,
                            backgroundColor: colors.bg,
                            color: colors.text,
                            borderRadius: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "0.7rem",
                            fontWeight: "medium",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            px: 1,
                            cursor: "pointer",
                            border: period.is_current
                              ? "2px solid"
                              : "1px solid",
                            borderColor: period.is_current
                              ? "primary.dark"
                              : "transparent",
                            "&:hover": {
                              opacity: 0.9,
                            },
                          }}
                        >
                          {barWidth > 60 && (
                            <Typography
                              variant="caption"
                              sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {period.employment_type}
                            </Typography>
                          )}
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Box>
              </Box>
            ))}

            {/* Legend */}
            <Box
              sx={{
                p: 2,
                borderTop: "2px solid",
                borderColor: "divider",
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Typography variant="body2" fontWeight="bold">
                Employment Types:
              </Typography>
              {Object.entries(EMPLOYMENT_TYPE_COLORS).map(([type, colors]) => (
                <Chip
                  key={type}
                  label={type}
                  size="small"
                  sx={{
                    backgroundColor: colors.bg,
                    color: colors.text,
                    fontWeight: "medium",
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}